import React, { useState } from "react";
import {
  useListAccountManagers,
  useCreateAccountManager,
  useArchiveAccountManager,
  useRestoreAccountManager,
  getListAccountManagersQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, Archive, RotateCcw } from "lucide-react";

export function AccountManagersList() {
  const [showArchived, setShowArchived] = useState(false);
  const { data: managers, isLoading, error } = useListAccountManagers(
    showArchived ? { includeArchived: true } : undefined,
  );
  const [open, setOpen] = useState(false);

  if (isLoading) return <div className="p-6 animate-pulse"><div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div><div className="h-64 bg-gray-200 rounded"></div></div>;
  if (error || !managers) return <div className="p-6 text-red-500">Failed to load account managers.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">Account Managers</h1>
        <Button onClick={() => setOpen(true)} className="bg-[#E6007E] hover:bg-[#c4006b] text-white">
          <Plus className="w-4 h-4 mr-2" /> Add account manager
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg text-[#0B132B]">All account managers</CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              id="am-show-archived"
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <Label htmlFor="am-show-archived" className="text-sm text-gray-500">Show archived</Label>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Agents Managed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {managers.map((manager) => (
                <TableRow key={manager.id} className={manager.archivedAt ? "opacity-60" : undefined}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      {manager.name}
                      {manager.archivedAt && <Badge variant="secondary">Archived</Badge>}
                    </span>
                  </TableCell>
                  <TableCell>{manager.email}</TableCell>
                  <TableCell className="text-right">{manager.agentsManaged || 0}</TableCell>
                  <TableCell className="text-right">
                    <ManagerRowActions id={manager.id} name={manager.name} archived={!!manager.archivedAt} />
                  </TableCell>
                </TableRow>
              ))}
              {managers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center p-8 text-gray-500">No account managers found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AddAccountManagerDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function ManagerRowActions({
  id,
  name,
  archived,
}: {
  id: number;
  name: string;
  archived: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const archive = useArchiveAccountManager();
  const restore = useRestoreAccountManager();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListAccountManagersQueryKey() });

  const onArchive = async () => {
    try {
      await archive.mutateAsync({ id });
      invalidate();
      toast({ title: "Account manager archived", description: `${name} has been archived.` });
    } catch {
      toast({ title: "Could not archive", description: "Please try again.", variant: "destructive" });
    }
  };

  const onRestore = async () => {
    try {
      await restore.mutateAsync({ id });
      invalidate();
      toast({ title: "Account manager restored", description: `${name} is active again.` });
    } catch {
      toast({ title: "Could not restore", description: "Please try again.", variant: "destructive" });
    }
  };

  if (archived) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onRestore}
        disabled={restore.isPending}
      >
        {restore.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" /> Restore</>}
      </Button>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={archive.isPending}>
          {archive.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Archive className="w-4 h-4 mr-2" /> Archive</>}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This hides the account manager from active lists. Their managed agents and tickets are kept, and you can restore them at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onArchive} className="bg-[#E6007E] hover:bg-[#c4006b] text-white">
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AddAccountManagerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const createManager = useCreateAccountManager();

  const reset = () => {
    setName("");
    setEmail("");
    setPhone("");
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) return;
    try {
      await createManager.mutateAsync({
        data: {
          name: name.trim(),
          email: email.trim(),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListAccountManagersQueryKey() });
      toast({ title: "Account manager added", description: `${name.trim()} has been created.` });
      reset();
      onOpenChange(false);
    } catch {
      toast({ title: "Could not add account manager", description: "Please check the details and try again.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Add account manager</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Naledi Dlamini" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@mooya.co.za" />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile number (optional)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="082 000 0000" />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!name.trim() || !email.trim() || createManager.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createManager.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add account manager"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
